import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import BACKEND_URL from '../config';
import Cookies from 'js-cookie';

interface InvitationValidation {
    valid: boolean;
    email?: string;
    error?: string;
}

const InvitationSignup = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [tokenValidation, setTokenValidation] = useState<InvitationValidation | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState<'email' | 'password'>('email');
    
    // Form state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: ''
    });

    // Validate invitation token on component mount
    useEffect(() => {
        if (!token) {
            toast.error('Invalid invitation link');
            navigate('/');
            return;
        }

        const validateToken = async () => {
            try {
                const response = await fetch(`${BACKEND_URL}/vnog/invite/validate/?token=${token}`, {
                    credentials: 'include',
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setTokenValidation(data);
                } else {
                    const error = await response.json();
                    setTokenValidation({ valid: false, error: error.error || 'Invalid invitation' });
                }
            } catch (error) {
                setTokenValidation({ valid: false, error: 'Failed to validate invitation' });
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token, navigate]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.email) {
            toast.error('Please enter your email address');
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/vnog/invite/validate/?token=${token}&email=${formData.email}`, {
                credentials: 'include',
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.valid) {
                    setStep('password');
                    toast.success('Email verified! Please complete your registration.');
                } else {
                    toast.error(data.error || 'Email does not match invitation');
                }
            } else {
                const error = await response.json();
                toast.error(error.error || 'Email validation failed');
            }
        } catch (error) {
            toast.error('Failed to validate email');
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        console.log('handlePasswordSubmit');
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (formData.password.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        if (!formData.firstName || !formData.lastName) {
            toast.error('Please enter your first and last name');
            return;
        }

        setSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('token', token || '');
            formDataToSend.append('email', formData.email);
            formDataToSend.append('password', formData.password);
            formDataToSend.append('first_name', formData.firstName);
            formDataToSend.append('last_name', formData.lastName);

            const response = await fetch(`${BACKEND_URL}/vnog/invite/signup/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: formDataToSend,
            });

            if (response.ok) {
                const data = await response.json();
                toast.success('Account created successfully! You can now log in.');
                window.location.href = '/home'
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to create account');
            }
        } catch (error) {
            toast.error('Error creating account');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-lg">Validating invitation...</div>
            </div>
        );
    }

    if (!tokenValidation?.valid) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
                    <div className="text-center">
                        <div className="text-red-600 text-6xl mb-4">⚠️</div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
                        <p className="text-gray-600 mb-6">
                            {tokenValidation?.error || 'This invitation link is not valid or has expired.'}
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                        >
                            Go to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <ToastContainer />
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {step === 'email' ? 'Verify Your Email' : 'Create Your Account'}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {step === 'email' 
                            ? 'You\'ve been invited to join VNOG. Please enter your email address to continue.'
                            : 'Please complete your registration below.'
                        }
                    </p>
                </div>

                {step === 'email' ? (
                    <form className="mt-8 space-y-6" onSubmit={handleEmailSubmit}>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={formData.email}
                                onChange={handleInputChange}
                                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder="Enter your email address"
                            />
                        </div>

                        <div>
                            <button
                                type="submit"
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Verify Email
                            </button>
                        </div>
                    </form>
                ) : (
                    <form className="mt-8 space-y-6" onSubmit={handlePasswordSubmit}>
                        <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-green-800">
                                            Email verified: {formData.email}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                    First Name
                                </label>
                                <input
                                    id="firstName"
                                    name="firstName"
                                    type="text"
                                    required
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Enter your first name"
                                />
                            </div>
                            
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                    Last Name
                                </label>
                                <input
                                    id="lastName"
                                    name="lastName"
                                    type="text"
                                    required
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Enter your last name"
                                />
                            </div>
                            
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    required
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Enter your password"
                                />
                            </div>
                            
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                    Confirm Password
                                </label>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type="password"
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="Confirm your password"
                                />
                            </div>
                        </div>

                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={() => setStep('email')}
                                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                onClick={(e) => handlePasswordSubmit(e)}
                                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {submitting ? 'Creating Account...' : 'Create Account'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default InvitationSignup;
